import { useCallback, useEffect, useRef } from 'react';

import { bind } from 'bind-event-listener';

import { type DocNode } from '@atlaskit/adf-schema';
import { fg } from '@atlaskit/platform-feature-flags';
import { type RovoApp } from '@atlassian/conversation-assistant-chat-prompt-input/controllers/chat-mode-store/types';

import { SBS_CONSENT_RECEIVED_EVENT_NAME, SBS_TRIGGER_EVENT_NAME } from '../types';

import { extractPlainTextFromAdf } from './extractPlainTextFromAdf';

/** The custom event name fired by the consent modal when it becomes visible. */
const SBS_MODAL_SHOWN_EVENT_NAME = 'rovo-sbs-modal-shown';

/**
 * Timeout for the modal appearance fail-safe.
 * If the consent modal hasn't appeared within this time after the SBS trigger fires,
 * something went wrong (service worker crash, message lost, render failure).
 * Release the held query so the user's message isn't silently swallowed.
 *
 * This is NOT a timeout for user response — once the modal is visible, this timeout
 * is cancelled and the user can take as long as they need.
 */
const SBS_MODAL_APPEARANCE_TIMEOUT_MS = 10_000;

type WindowWithRovoExtension = Window & {
	rovoExtension?: {
		openChat?: unknown;
	};
};

function isRovoExtensionLikelyAvailable(): boolean {
	return typeof (window as WindowWithRovoExtension).rovoExtension?.openChat === 'function';
}

/** The chat mode determined from submit mode flags. */
type SBSChatMode = 'auto' | 'deep_thinking' | 'deep_research';

/**
 * Determines the SBS chat mode from the submit mode flags.
 * Maps the boolean flags to the string enum used by the SBS system.
 */
function getSBSChatMode({
	deepResearchEnabled,
	thinkDeeperEnabled,
}: {
	deepResearchEnabled: boolean;
	thinkDeeperEnabled?: boolean;
}): SBSChatMode {
	if (deepResearchEnabled) {
		return 'deep_research';
	}
	if (thinkDeeperEnabled) {
		return 'deep_thinking';
	}
	return 'auto';
}

/**
 * Extracts knowledge source names from the app filters.
 * Returns an array of friendly names or source identifiers.
 */
function extractKnowledgeSources({ appFilters }: { appFilters?: RovoApp[] }): string[] {
	if (!appFilters) {
		return [];
	}
	return appFilters.map((app) => {
		if ('friendlyName' in app && typeof app.friendlyName === 'string') {
			return app.friendlyName;
		}
		if ('source' in app && typeof app.source === 'string') {
			return app.source;
		}
		return String(app);
	});
}

/** The mode configuration for an SBS trigger. */
interface SBSTriggerMode {
	deepResearchEnabled: boolean;
	thinkDeeperEnabled?: boolean;
	webSearchEnabled: boolean;
	appFilters?: RovoApp[];
}

interface SBSTriggerParams {
	prompt: DocNode;
	mode: SBSTriggerMode;
	conversationId: string;
	siteId: string;
	productKey: string;
	/** Number of existing messages in the current conversation. Used to skip SBS for mid-conversation queries. */
	existingMessageCount: number;
	/**
	 * The URL of the implicit context (e.g. the Confluence page currently shown in the context
	 * banner above the chat input). When provided and the user has "add current page as context"
	 * enabled, the query sent to competitors and the Rovo Chrome Extension is prefixed with
	 * "Context: <url>\n" so that the competitor AI has the same page context as Rovo.
	 */
	implicitContextUrl?: string;
}

/** Result of calling triggerSbsEvaluation — indicates whether the query should be held. */
interface SBSTriggerResult {
	/** Whether the query should be held (not submitted yet) pending SBS consent. */
	shouldHoldQuery: boolean;
}

interface UseSBSTriggerResult {
	/** Trigger an SBS evaluation check. Returns whether the query should be held. */
	triggerSbsEvaluation: (params: SBSTriggerParams) => SBSTriggerResult;
	/**
	 * Release the held query. Call this after the user responds to the SBS consent modal.
	 * The callback passed to releaseQuery will be called to submit the query.
	 */
	releaseHeldQuery: () => void;
	/**
	 * Register a callback to be invoked when the held query should be released.
	 * This is called by the consent handler (participate, decline, or opt_out).
	 */
	setQueryReleaseCallback: (callback: (() => void) | null) => void;
}

/**
 * Hook that provides a function to trigger SBS evaluation when conditions are met.
 * Dispatches a CustomEvent on the window that the extension frontend listens for
 * and forwards through the postMessage bridge to the service worker.
 *
 * When SBS triggers, the query should be HELD (not submitted) until the user
 * responds to the consent modal. The caller uses `shouldHoldQuery` to decide
 * whether to defer `sendMessage()`, and `releaseHeldQuery` / `setQueryReleaseCallback`
 * to wire up the release mechanism.
 */
export function useSBSTrigger({
	productKey,
	experienceId,
	siteId,
}: {
	productKey: string;
	experienceId: string;
	siteId: string;
}): UseSBSTriggerResult {
	// Track whether an evaluation is already in progress to prevent duplicate triggers
	const evaluationInProgressRef = useRef(false);
	const resetTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
	// Stores the callback that releases the held query (calls sendMessage)
	const queryReleaseCallbackRef = useRef<(() => void) | null>(null);
	// Generation counter to prevent stale timeouts from releasing queries for newer evaluations
	const evaluationGenerationRef = useRef(0);

	// Cached list of all connectors enabled on the Rovo tenant.
	// Fetched eagerly on mount so it's ready before the first evaluation triggers.
	const rovoConnectorsRef = useRef<string[]>([]);

	// Fetch Rovo-enabled connectors on mount (fire-and-forget, cached in ref)
	useEffect(() => {
		if (!productKey) {
			return;
		}
		fetch('/gateway/api/assist/rovo/v1/agents/configuration/knowledge', {
			headers: { 'x-product': productKey, 'x-experience-id': experienceId, 'x-cloudid': siteId },
		})
			.then((res) => res.json())
			.then((data) => {
				rovoConnectorsRef.current = (data.knowledgeSources ?? []).map(
					(s: { friendlyName?: string; source?: string }) => s.friendlyName || s.source || '',
				);
			})
			.catch(() => {
				// Fail silently — rovoEnabledConnectors will default to []
			});
	}, [productKey, experienceId, siteId]);

	const releaseHeldQuery = useCallback(() => {
		if (queryReleaseCallbackRef.current) {
			queryReleaseCallbackRef.current();
			queryReleaseCallbackRef.current = null;
		}

		evaluationInProgressRef.current = false;

		if (resetTimeoutRef.current !== null) {
			clearTimeout(resetTimeoutRef.current);
			resetTimeoutRef.current = null;
		}
	}, []);

	const setQueryReleaseCallback = useCallback((callback: (() => void) | null) => {
		queryReleaseCallbackRef.current = callback;
	}, []);

	const triggerSbsEvaluation = useCallback(
		({
			prompt,
			mode,
			conversationId,
			siteId,
			productKey,
			existingMessageCount,
			implicitContextUrl,
		}: SBSTriggerParams): SBSTriggerResult => {
			try {
				// Don't trigger if an evaluation is already in progress
				if (evaluationInProgressRef.current) {
					return { shouldHoldQuery: false };
				}

				if (!fg('rovo-ext_sbs_enabled')) {
					return { shouldHoldQuery: false };
				}

				// The Rovo extension injects window.rovoExtension.openChat from rovo-window-triggers.js.
				// If it is absent, the extension is likely disabled/not injected, so do not hold chat for SBS.
				if (!isRovoExtensionLikelyAvailable()) {
					return { shouldHoldQuery: false };
				}

				const plainTextQuery = extractPlainTextFromAdf(prompt);

				// Don't trigger for empty queries
				if (!plainTextQuery || plainTextQuery.trim().length === 0) {
					return { shouldHoldQuery: false };
				}

				// First-message guard: only trigger SBS on the first message in a conversation.
				if (existingMessageCount > 0) {
					return { shouldHoldQuery: false };
				}

				const chatMode = getSBSChatMode({
					deepResearchEnabled: mode.deepResearchEnabled,
					thinkDeeperEnabled: mode.thinkDeeperEnabled,
				});

				const knowledgeSources = extractKnowledgeSources({ appFilters: mode.appFilters });

				const isChatGPTEligible = fg('rovo-ext_sbs_chatgpt_eligible');

				// Prepend implicit context (e.g. the Confluence page shown in the context banner)
				// so that competitors and the Rovo Chrome Extension receive the same page context
				// as Rovo. Format: "Context: <url>\n<original query>"
				const queryWithContext =
					implicitContextUrl && implicitContextUrl.trim()
						? `Context: ${implicitContextUrl.trim()}\n${plainTextQuery}`
						: plainTextQuery;

				evaluationInProgressRef.current = true;

				window.dispatchEvent(
					new CustomEvent(SBS_TRIGGER_EVENT_NAME, {
						detail: {
							query: queryWithContext,
							chatMode,
							knowledgeSources,
							rovoEnabledConnectors: rovoConnectorsRef.current,
							conversationId,
							siteId,
							product: productKey,
							isChatGPTEligible,
						},
					}),
				);

				// Modal appearance fail-safe
				if (resetTimeoutRef.current !== null) {
					clearTimeout(resetTimeoutRef.current);
				}
				evaluationGenerationRef.current += 1;
				const thisGeneration = evaluationGenerationRef.current;
				resetTimeoutRef.current = setTimeout(() => {
					if (evaluationGenerationRef.current !== thisGeneration) {
						return;
					}
					// eslint-disable-next-line no-console
					console.warn(
						'[SBS] Modal appearance timeout fired — consent modal did not appear within',
						SBS_MODAL_APPEARANCE_TIMEOUT_MS,
						'ms. Releasing held query.',
					);
					releaseHeldQuery();
				}, SBS_MODAL_APPEARANCE_TIMEOUT_MS);

				return { shouldHoldQuery: true };
			} catch {
				// SBS must never break normal Rovo Chat — silently ignore all errors
				evaluationInProgressRef.current = false;
				return { shouldHoldQuery: false };
			}
		},
		[releaseHeldQuery],
	);

	// Cancel the modal-appearance timeout when the modal becomes visible
	useEffect(() => {
		const handleModalShown = (): void => {
			if (resetTimeoutRef.current !== null) {
				clearTimeout(resetTimeoutRef.current);
				resetTimeoutRef.current = null;
			}
		};

		const unbind = bind(window, {
			type: SBS_MODAL_SHOWN_EVENT_NAME,
			listener: handleModalShown,
		});

		return unbind;
	}, []);

	// Listen for consent response events to release the held query
	useEffect(() => {
		const handleConsentReceived = (): void => {
			releaseHeldQuery();
		};

		const unbind = bind(window, {
			type: SBS_CONSENT_RECEIVED_EVENT_NAME,
			listener: handleConsentReceived,
		});

		return unbind;
	}, [releaseHeldQuery]);

	// Cleanup on unmount: release any held query
	useEffect(() => {
		return (): void => {
			releaseHeldQuery();
		};
	}, [releaseHeldQuery]);

	return { triggerSbsEvaluation, releaseHeldQuery, setQueryReleaseCallback };
}
